import { useEffect, useState } from 'react';

import { type ConnectorApp } from '@post-office/browser--rovo-expansion/common/index';

import type { ConnectorProvider, ConnectorRecommendation } from '../types';

type UseConnectorRecommendationProps = {
	connectorProviders: ConnectorProvider[];
	tenantId: string;
	fireConnectorRecommendationErrorEvent: (error: string) => void;
	skip?: boolean;
};

type GetConnectorRecommendationProps = Omit<UseConnectorRecommendationProps, 'skip'>;

const SEARCH_CONFIG_OPERATION_NAME = 'PostOfficeThirdPartyConnectorsConfig';

export const useConnectorRecommendation = ({
	connectorProviders,
	tenantId,
	fireConnectorRecommendationErrorEvent,
	skip = false,
}: UseConnectorRecommendationProps): ConnectorRecommendation | null => {
	const [connectorRecommendation, setConnectorRecommendation] =
		useState<ConnectorRecommendation | null>(null);

	useEffect(() => {
		if (skip) {
			return;
		}

		let cancelled = false;

		void getConnectorToRecommend({
			connectorProviders,
			tenantId,
			fireConnectorRecommendationErrorEvent,
		}).then((result) => {
			if (!cancelled) {
				setConnectorRecommendation(result);
			}
		});

		return () => {
			cancelled = true;
		};
	}, [connectorProviders, tenantId, fireConnectorRecommendationErrorEvent, skip]);

	return connectorRecommendation;
};

const getConnectorToRecommend = async ({
	connectorProviders,
	tenantId,
	fireConnectorRecommendationErrorEvent,
}: GetConnectorRecommendationProps): Promise<ConnectorRecommendation | null> => {
	if (connectorProviders.length < 1) {
		return null;
	}

	try {
		const response = await fetch(
			`/gateway/api/third-party-configuration/connected-data-sources/${tenantId}?operationName=${SEARCH_CONFIG_OPERATION_NAME}`,
			{
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
				},
				credentials: 'include',
			},
		);
		if (response.ok) {
			const connectedDataSourcesDetails = (await response.json()) as ConnectorApp[];
			const result = getRecommendationsFromConnectorDetails(
				connectedDataSourcesDetails,
				connectorProviders,
			);

			if (!result) {
				return null;
			}

			return result[0]!;
		} else {
			fireConnectorRecommendationErrorEvent('Failed to fetch connector auth details');
			return null;
		}
	} catch (error) {
		fireConnectorRecommendationErrorEvent('Failed during API call to fetch connector auth details');
		return null;
	}
};

const getRecommendationsFromConnectorDetails = (
	connectorDataDetails: ConnectorApp[],
	connectorProviders: ConnectorProvider[],
): ConnectorRecommendation[] | null => {
	if (connectorDataDetails.length < 1 || connectorProviders.length < 1) {
		return null;
	}

	const recommendations: ConnectorRecommendation[] = [];

	for (const connectorProvider of connectorProviders) {
		const connectorData = connectorDataDetails.find(
			(data) => data.type === connectorProvider.providerType,
		);
		if (!connectorData || connectorData.isUserOAuthed) {
			continue;
		}
		recommendations.push({
			provider: connectorProvider,
			outboundAuthUrl: connectorData.outboundAuthUrl,
		});
	}

	return recommendations.length > 0 ? recommendations : null;
};
